import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Divider, Grid2, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { usePageTitle } from '@enroller/utils';
import { getConfig } from '../../../utils/config';
import { genderOptions } from '../../../constants';
import {
  defaultValues,
  FamilyFormValues,
  familyMemberDefaultValues,
  familySchema,
  friendDefaultValues,
  STUDENT_FAMILY_SECTIONS,
  STUDENT_FAMILY_SUB_SECTIONS,
  createStudentFamilyPayload,
} from './FamilyFormValues';
import { yesNoOptions } from '../../../constants/yesNoOptions';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';
import { API_FIELDS_CONTACT as CONTACT, API_FIELDS_STUDENT_FAMILY as STUDENT_FAMILY } from '@enroller/admissions-components';
import { StudentApplicationValues } from '../StudentApplication';
const { googleMapsAPIKey } = getConfig();

const parentStatusOptions = [
  {
    value: 'Living',
  },
  {
    value: 'Deceased',
  },
];

const maritalStatusOptions = [
  {
    label: 'Together',
    value: 'Together',
  },
  {
    label: 'Separated',
    value: 'Separated',
  },
  {
    label: 'Divorced',
    value: 'Divorced',
  },
  {
    label: 'Other',
    value: 'Other',
    textField: {
      label: '',
      name: `${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY.maritalStatus}.maritalStatusOther`,
      placeholder: 'Please explain',
    },
  },
];

const homeLocationOptions = [
  {
    value: 'In a House',
  },
  {
    value: 'In an Apartment',
  },
  {
    value: 'In a city',
  },
  {
    value: 'In a suburb',
  },
  {
    value: 'On a Farm',
  },
  {
    value: 'In a Village / Small City',
  },
];

interface FamilyFormProps {
  applicationData: FamilyFormValues;
  submitApplication: (data: FieldValues) => Promise<void>;
  modulesID?: StudentApplicationValues['modulesID'];
}

export const FamilyForm = ({ applicationData, modulesID, submitApplication }: FamilyFormProps) => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Family', subtitle: '' });
    setPageTitle('Family');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? familySchema.required : familySchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit, reset, trigger } = useForm<FamilyFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const { fields: parents } = useFieldArray({
    control,
    name: `${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}`,
  });

  const {
    fields: familyMembers,
    append: addFamilyMember,
    remove: removeFamilyMember,
  } = useFieldArray({
    control,
    name: `${STUDENT_FAMILY_SECTIONS.familyMembers}`,
  });

  const {
    fields: friendsInUSA,
    append: addFriendInUSA,
    remove: removeFriendInUSA,
  } = useFieldArray({
    control,
    name: `${STUDENT_FAMILY_SECTIONS.friendsInUSA}`,
  });

  // TODO: remove the object access applicationData?.family once this form is integrated to GraphQL
  useEffect(() => {
    if (applicationData) {
      reset({ ...defaultValues, ...applicationData });
    }
  }, [applicationData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          console.log('Form submitted:', data);
          if (!modulesID) {
            console.warn('Student Family: modulesID not found');
            return;
          }
          setApplicationPageData('family', data);
          // if (validationMode === ValidationType.Full) triggerErrors();

          submitApplication(createStudentFamilyPayload(data, modulesID))
            .then(() => console.log('[SAP] Student Family API: submit success'))
            .catch((error) => console.error('[SAP] Student Family API: submit error:', error))
            .finally(() => setIsFormLoading(false));
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, modulesID, setApplicationPageData, triggerErrors, submitApplication, setIsFormLoading],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Parents / Guardians</Typography>
          </Grid2>
          {parents.map((item, index) => (
            <Fragment key={item.id}>
              <Grid2 size={12}>
                <Typography variant="subtitle1">Parent / Guardian {index + 1}</Typography>
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Text}
                  label="Family Name"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.lastName}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Text}
                  label="First Name(s)"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.firstName}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Radio}
                  label="Gender"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.gender}`}
                  control={control}
                  options={genderOptions}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormField
                  type={FormFieldType.Radio}
                  label="Is a legal guardian?"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.isLegalGuardian}`}
                  control={control}
                  options={yesNoOptions}
                />
              </Grid2>
              <Grid2 size={12}>
                <FormField
                  googleMapsAPIKey={googleMapsAPIKey}
                  type={FormFieldType.AddressAutocomplete}
                  label="Address"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${STUDENT_FAMILY_SUB_SECTIONS.address}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Text}
                  label="Occupation"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.occupation}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Text}
                  label="Cell Phone"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.mobile}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Text}
                  label="Work Phone"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.workPhone}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Text}
                  label="Email Address"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.email}`}
                  control={control}
                />
              </Grid2>
              <Grid2 size={4}>
                <FormField
                  type={FormFieldType.Radio}
                  label="Is this parent"
                  name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY_SUB_SECTIONS.parents}.${index}.${CONTACT.parentStatus}`}
                  control={control}
                  options={parentStatusOptions}
                />
              </Grid2>
              <Grid2 size={12}>
                <Divider sx={{ m: 0 }} />
              </Grid2>
            </Fragment>
          ))}

          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Are your parents"
              name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY.maritalStatus}.value`}
              control={control}
              options={maritalStatusOptions}
            />
          </Grid2>

          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Checkboxset}
              label="Do you live in: (choose all that apply)"
              name={`${STUDENT_FAMILY_SECTIONS.parentsSection}.${STUDENT_FAMILY.houseType}`}
              control={control}
              options={homeLocationOptions}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Siblings / Other Family Members</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              <Typography variant="body2">
                Please add any other family members (brothers, sisters, step-parents, or other people that live with you in the same house).
              </Typography>
            </Alert>
          </Grid2>

          {familyMembers.map((item, index) => (
            <Grid2 size={12} key={item.id}>
              <Paper sx={{ p: 2 }}>
                <Grid2 container spacing={4}>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="First Name"
                      name={`${STUDENT_FAMILY_SECTIONS.familyMembers}.${index}.${CONTACT.firstName}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="Last Name"
                      name={`${STUDENT_FAMILY_SECTIONS.familyMembers}.${index}.${CONTACT.lastName}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="Relationship"
                      name={`${STUDENT_FAMILY_SECTIONS.familyMembers}.${index}.${CONTACT.relationship}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Number}
                      label="Age"
                      name={`${STUDENT_FAMILY_SECTIONS.familyMembers}.${index}.${CONTACT.age}`}
                      control={control}
                      min={0}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Radio}
                      label="Gender"
                      name={`${STUDENT_FAMILY_SECTIONS.familyMembers}.${index}.${CONTACT.gender}`}
                      control={control}
                      options={genderOptions}
                    />
                  </Grid2>
                  <Grid2 size={12}>
                    <Button variant="outlined" color="error" onClick={() => removeFamilyMember(index)}>
                      Delete
                    </Button>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>
          ))}
          <Grid2 container>
            <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={() => addFamilyMember(familyMemberDefaultValues)}>
              Add A Family Member
            </Button>
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Friends / Relatives in the USA</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              <Typography variant="body2">Please add information of any friends or relatives in the USA.</Typography>
            </Alert>
          </Grid2>

          {friendsInUSA.map((item, index) => (
            <Grid2 size={12} key={item.id}>
              <Paper sx={{ p: 2 }}>
                <Grid2 container spacing={4}>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="First Name"
                      name={`${STUDENT_FAMILY_SECTIONS.friendsInUSA}.${index}.${CONTACT.firstName}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="Last Name"
                      name={`${STUDENT_FAMILY_SECTIONS.friendsInUSA}.${index}.${CONTACT.lastName}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="Relationship"
                      name={`${STUDENT_FAMILY_SECTIONS.friendsInUSA}.${index}.${CONTACT.relationship}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="Email Address"
                      name={`${STUDENT_FAMILY_SECTIONS.friendsInUSA}.${index}.${CONTACT.email}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <FormField
                      type={FormFieldType.Text}
                      label="Phone Number"
                      name={`${STUDENT_FAMILY_SECTIONS.friendsInUSA}.${index}.${CONTACT.phone}`}
                      control={control}
                    />
                  </Grid2>
                  <Grid2 size={12}>
                    <FormField
                      googleMapsAPIKey={googleMapsAPIKey}
                      type={FormFieldType.AddressAutocomplete}
                      label="Address"
                      name={`${STUDENT_FAMILY_SECTIONS.friendsInUSA}.${index}.${STUDENT_FAMILY_SUB_SECTIONS.address}`}
                      control={control}
                      restrictToUS
                    />
                  </Grid2>
                  <Grid2 size={12}>
                    <Button variant="outlined" color="error" onClick={() => removeFriendInUSA(index)}>
                      Delete
                    </Button>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>
          ))}
          <Grid2 container>
            <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={() => addFriendInUSA(friendDefaultValues)}>
              Add A Friend / Relative
            </Button>
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
